import React from 'react';

import Alert from 'CommonComponents/Alert';
import TextButton from 'CommonComponents/TextButton';

import FileDuplicateIcon from 'Assets/icons/file-duplicate.svg';

/**
 * Tells the user bulk apply exists, at the moment it becomes available.
 *
 * The apply trigger only appears while the pointer is over a cell, which makes it
 * easy to miss on a screen this dense — so this says the feature is there and
 * shows the exact glyph to look for. It is not a toast: it stays until dismissed,
 * and a dismissal is permanent (the caller owns that, see ReconciliationIndex).
 *
 * The sentence is deliberately one translation key rather than a string split
 * around the icon: a split sentence cannot be translated into Arabic, whose word
 * order differs. It ends on a colon so the glyph reads as the thing named, and
 * follows the text in logical order so RTL mirrors it correctly.
 *
 * `contractOnly` names the one column the reconciled table offers, and describes a
 * different action than the other tables': every row here already carries a value
 * in every other column, so the trigger only ever appears on a contract reference
 * cell that has one — which nothing on this tab has until someone sets it. The
 * generic "hover the cell you want to copy" sentence would send the reconciler
 * hunting for a trigger that cannot exist yet.
 */
export default function BulkApplyHint({ count, contractOnly = false, onDismiss }) {
    return (
        <div className="tw-mb-3">
            <Alert type="info" role="status">
                <div className="tw-flex tw-items-center tw-gap-x-3 tw-flex-wrap">
                    <span>
                        {contractOnly
                            ? $t(
                                  'This list is filtered — set the contract reference on one row, then click the icon that appears beside it to apply it to all {{count}} rows:',
                                  { count },
                              )
                            : $t(
                                  "This list is filtered — you can apply one row's value to all {{count}} rows. Hover the cell you want to copy and click the icon that appears:",
                                  { count },
                              )}
                    </span>

                    {/* aria-hidden: the sentence already names it, so announcing the
                        icon again would just read as noise. Same classes as the real
                        trigger (BulkApplyPanel.js) — no border/background, or this
                        would read as a button rather than the bare glyph to hunt for. */}
                    <span aria-hidden="true">
                        <FileDuplicateIcon className="tw-h-3 tw-w-3 tw-text-blue" />
                    </span>

                    <div className="tw-ms-auto">
                        <TextButton onClick={onDismiss} className="tw-text-xs">
                            {$t('Dismiss')}
                        </TextButton>
                    </div>
                </div>
            </Alert>
        </div>
    );
}
